import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import ExperimentalFeatureBanner from "components/Common/ExperimentalFeatureBanner";
import IssueSearch, { type Suggestion } from "components/Issues/IssueSearch";
import InputField from "ui/InputField";
import CollectionCardRefactored, {
  type CollectionEntity,
} from "components/Collections/CollectionCardRefactored";
import { useIssueFromSuggestion } from "hooks/useIssueFromSuggestion";
import {
  graphCollectionsApi,
  mapGraphSnapshotToCanvasData,
  type GraphCollectionSnapshot,
  type GraphFeedSnapshotItem,
} from "lib/api/graphCollections";
import { collectionApi } from "lib/api/collections";
import { createCollection } from "util/posts";
import { apiRoutes } from "util/apiRoutes";
import { api } from "lib/api/client";
import {
  formatGraphMessage,
  graphMessage,
  resolveGraphLocale,
  type GraphLocale,
} from "./graphCopy";

const SEARCH_DEBOUNCE_MS = 320;
const INITIAL_SEED_POSITION = { x: 0, y: 0 };

/**
 * Ensure an issue suggestion resolves into a persistent issue; small helper
 * shared with the graph editor's seed flow.
 */
const useResolvedIssueName = (suggestion: Suggestion | null): string | null => {
  return useMemo(() => {
    if (!suggestion) {
      return null;
    }
    if ("name" in suggestion && typeof suggestion.name === "string" && suggestion.name.trim()) {
      return suggestion.name;
    }
    if ("cname" in suggestion && typeof suggestion.cname === "string" && suggestion.cname) {
      return suggestion.cname.replace(/_/g, " ");
    }
    return null;
  }, [suggestion]);
};

interface SelectedSource {
  post: CollectionEntity;
  snapshot: GraphCollectionSnapshot | null;
}

/**
 * Draft/create surface for graph collections. Replaces the legacy
 * `?mode=edit&blank=1` URL with a dedicated `?mode=draft` surface that
 * integrates Create (seed by issue), Extend (one public source), and Remix
 * (2+ public sources) flows.
 *
 * Creation paths:
 *   - 1 issue seed, 0 sources → "Start from this issue"
 *   - 0 issues, 1 source     → "Start from this graph" (sync, blank title)
 *   - 0 issues, 2+ sources   → "Remix these graphs" (sync against each source)
 *   - 1 issue + 1+ sources   → "Seed and remix" (seed + sync-remix)
 */
const GraphDraftEditor: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ensureIssue } = useIssueFromSuggestion();
  const graphLocale: GraphLocale = useMemo(
    () => resolveGraphLocale(typeof navigator !== "undefined" ? navigator.language : undefined),
    []
  );

  const [selectedIssue, setSelectedIssue] = useState<Suggestion | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchInput, setDebouncedSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState<GraphFeedSnapshotItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedSources, setSelectedSources] = useState<SelectedSource[]>([]);
  const [isStartingDraft, setIsStartingDraft] = useState(false);

  const resolvedIssueName = useResolvedIssueName(selectedIssue);

  // Back-compat: if a bookmarked link lands here with ?mode=edit&blank=1,
  // the router already routed us to this component. Nothing to do.
  useEffect(() => {
    const blankHint = searchParams.get("blank");
    if (blankHint === "1") {
      // no-op: the draft surface already is the "blank" experience
    }
  }, [searchParams]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchInput(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [searchInput]);

  const lastSearchSeqRef = useRef(0);
  useEffect(() => {
    if (debouncedSearchInput.length < 2) {
      setSearchResults([]);
      setSearchError(null);
      setSearchLoading(false);
      return;
    }
    const seq = lastSearchSeqRef.current + 1;
    lastSearchSeqRef.current = seq;
    setSearchLoading(true);
    setSearchError(null);
    (async () => {
      try {
        const response = await graphCollectionsApi.search({
          query: debouncedSearchInput,
          limit: 15,
        });
        if (seq !== lastSearchSeqRef.current) {
          return;
        }
        setSearchResults(response.items || []);
      } catch (error) {
        if (seq !== lastSearchSeqRef.current) {
          return;
        }
        console.error("Failed to search public graph collections:", error);
        setSearchError(graphMessage(graphLocale, "loadGraphError"));
      } finally {
        if (seq === lastSearchSeqRef.current) {
          setSearchLoading(false);
        }
      }
    })();
  }, [debouncedSearchInput, graphLocale]);

  const selectedSourceIds = useMemo(
    () =>
      new Set(
        selectedSources
          .map((entry) => Number(entry.post.id))
          .filter((id) => Number.isFinite(id))
      ),
    [selectedSources]
  );

  const handleAddSource = useCallback((entry: GraphFeedSnapshotItem) => {
    const id = Number(entry.post.id);
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }
    setSelectedSources((current) => {
      if (current.some((s) => Number(s.post.id) === id)) {
        return current;
      }
      return [
        ...current,
        {
          post: entry.post as CollectionEntity,
          snapshot: entry.graph,
        },
      ];
    });
  }, []);

  const handleRemoveSource = useCallback((id: number) => {
    setSelectedSources((current) => current.filter((s) => Number(s.post.id) !== id));
  }, []);

  const primaryCtaLabel = useMemo(() => {
    const hasIssue = Boolean(selectedIssue);
    const sourceCount = selectedSources.length;
    if (hasIssue && sourceCount === 0) {
      return graphMessage(graphLocale, "startFromIssue");
    }
    if (!hasIssue && sourceCount === 1) {
      return graphMessage(graphLocale, "startFromThisGraph");
    }
    if (!hasIssue && sourceCount >= 2) {
      return graphMessage(graphLocale, "remixTheseGraphs");
    }
    if (hasIssue && sourceCount >= 1) {
      return graphMessage(graphLocale, "seedAndRemix");
    }
    return graphMessage(graphLocale, "startFromIssue");
  }, [graphLocale, selectedIssue, selectedSources.length]);

  const primaryCtaDisabled = !selectedIssue && selectedSources.length === 0;

  /**
   * Primary action dispatcher. Follows the decision tree in the CTA label
   * computation above. All branches end with a navigation to
   * `/graph?mode=edit&collectionId=<id>` so the user lands in the full
   * editor on the newly created collection.
   */
  const handlePrimaryAction = useCallback(async () => {
    if (isStartingDraft || primaryCtaDisabled) {
      return;
    }
    setIsStartingDraft(true);
    try {
      const hasIssue = Boolean(selectedIssue);
      const sourceCount = selectedSources.length;

      if (hasIssue && sourceCount === 0 && selectedIssue) {
        // Legacy seed flow: create collection + attach seed issue.
        const seedIssue = await ensureIssue(selectedIssue);
        if (!seedIssue?.id) {
          throw new Error("GRAPH_SEED_MISSING");
        }
        const seedLabel = seedIssue.name
          || seedIssue.cname?.replace(/_/g, " ")
          || resolvedIssueName
          || graphMessage(graphLocale, "unknownIssue");
        const createdCollection = await createCollection({
          issueId: null,
          title: `Graph · ${seedLabel}`,
          body: null,
          url: null,
          audience: "public",
          status: "draft",
          properties: {
            privacy: "public",
            collection_type: "graph",
            graph_mode: "blank",
            seed_issue_id: Number(seedIssue.id),
          },
        });
        await collectionApi.addItem(createdCollection.id, {
          entityId: Number(seedIssue.id),
          entityType: "issue",
          properties: {
            position: INITIAL_SEED_POSITION,
          },
        });
        toast.success(graphMessage(graphLocale, "draftGraphCreatedToast"));
        navigate(`/graph?mode=edit&collectionId=${encodeURIComponent(String(createdCollection.id))}`);
        return;
      }

      if (!hasIssue && sourceCount === 1) {
        // Extend from a single source: blank title, sync mode.
        const [only] = selectedSources;
        const response = await graphCollectionsApi.rebrew({
          sourceCollectionIds: [Number(only.post.id)],
          mode: "sync",
          title: null,
        });
        const id = Number((response.collection as { id: number }).id);
        toast.success(graphMessage(graphLocale, "draftGraphCreatedToast"));
        navigate(`/graph?mode=edit&collectionId=${encodeURIComponent(String(id))}`);
        return;
      }

      if (!hasIssue && sourceCount >= 2) {
        // Remix N public sources: new collection with N active sync rows.
        const response = await graphCollectionsApi.rebrew({
          sourceCollectionIds: selectedSources.map((s) => Number(s.post.id)),
          mode: "remix",
          title: null,
        });
        const id = Number((response.collection as { id: number }).id);
        toast.success(graphMessage(graphLocale, "draftGraphCreatedToast"));
        navigate(`/graph?mode=edit&collectionId=${encodeURIComponent(String(id))}`);
        return;
      }

      if (hasIssue && sourceCount >= 1 && selectedIssue) {
        // Seed + remix: start from the picked issue, then layer sync sources.
        const seedIssue = await ensureIssue(selectedIssue);
        if (!seedIssue?.id) {
          throw new Error("GRAPH_SEED_MISSING");
        }
        const response = await graphCollectionsApi.rebrew({
          sourceCollectionIds: selectedSources.map((s) => Number(s.post.id)),
          mode: sourceCount === 1 ? "sync" : "remix",
          title: null,
        });
        const id = Number((response.collection as { id: number }).id);
        // Attach the seed issue as a local item so the remix starts with the
        // seed visible even if no source already contains it.
        try {
          await collectionApi.addItem(id, {
            entityId: Number(seedIssue.id),
            entityType: "issue",
            properties: {
              position: INITIAL_SEED_POSITION,
            },
          });
          // Propagate seed to properties so future refreshes can recenter on it.
          await api.patch(apiRoutes.posts.byId(id), {
            properties: {
              seed_issue_id: Number(seedIssue.id),
            },
          });
        } catch (seedErr) {
          console.error("Failed to attach seed issue to remix:", seedErr);
        }
        toast.success(graphMessage(graphLocale, "draftGraphCreatedToast"));
        navigate(`/graph?mode=edit&collectionId=${encodeURIComponent(String(id))}`);
        return;
      }
    } catch (error) {
      console.error("Graph draft creation failed:", error);
      toast.error(graphMessage(graphLocale, "rebrewError"));
    } finally {
      setIsStartingDraft(false);
    }
  }, [
    ensureIssue,
    graphLocale,
    isStartingDraft,
    navigate,
    primaryCtaDisabled,
    resolvedIssueName,
    selectedIssue,
    selectedSources,
  ]);

  const renderCompactCard = (
    post: CollectionEntity,
    snapshot: GraphCollectionSnapshot | null,
    action: React.ReactNode
  ) => {
    const mapped = snapshot ? mapGraphSnapshotToCanvasData(snapshot) : null;
    const graphSnapshot = mapped
      ? { nodes: mapped.nodes, edges: mapped.edges, rootNodeId: mapped.rootNodeId }
      : null;
    return (
      <CollectionCardRefactored
        collection={post}
        variant="compact"
        graphSnapshot={graphSnapshot}
        action={action}
      />
    );
  };

  return (
    <div className="graph-draft-editor relative flex min-h-[calc(100vh-56px)] w-full flex-col bg-eoe-bg dark:bg-eoe-dark-bg">
      <ExperimentalFeatureBanner featureId="graph-explorer" />
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8">
        <section className="graph-draft-create">
          <h1 className="text-xl font-semibold text-eoe-text dark:text-eoe-dark-text">
            {graphMessage(graphLocale, "createTitle")}
          </h1>
          <p className="mt-1 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {graphMessage(graphLocale, "blankDraftHint")}
          </p>
          <div className="mt-4">
            <IssueSearch
              inputType="add"
              placeholder={graphMessage(graphLocale, "blankSearchHint")}
              className="w-full"
              prepopulateData={selectedIssue ?? undefined}
              onSelect={(suggestion) => setSelectedIssue(suggestion)}
              onEntityChipClose={() => setSelectedIssue(null)}
            />
          </div>
        </section>

        <section className="graph-draft-extend">
          <h2 className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
            {graphMessage(graphLocale, "extendPanelTitle")}
          </h2>
          <p className="mt-1 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {graphMessage(graphLocale, "extendPanelSubtitle")}
          </p>
          <div className="mt-3 relative">
            <InputField
              type="search"
              labelText={graphMessage(graphLocale, "extendSearchPlaceholder")}
              fieldPlaceholder={graphMessage(graphLocale, "extendSearchPlaceholder")}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setSearchInput("");
                  setDebouncedSearchInput("");
                }
              }}
              autoComplete="off"
              spellCheck={false}
              aria-label={graphMessage(graphLocale, "extendSearchPlaceholder")}
              className={searchInput.length > 0 ? "pr-12" : undefined}
            />
            {searchInput.length > 0 ? (
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-eoe-text-sub transition-colors hover:bg-eoe-icon hover:text-eoe-text focus:outline-none focus:ring-2 focus:ring-eoe-accent dark:text-eoe-dark-text-sub dark:hover:bg-eoe-dark-icon dark:hover:text-eoe-dark-text"
                onClick={() => {
                  setSearchInput("");
                  setDebouncedSearchInput("");
                }}
                aria-label={graphMessage(graphLocale, "extendClearSearch")}
              >
                <X className="size-4" aria-hidden />
              </button>
            ) : null}
            {debouncedSearchInput.length > 0 && debouncedSearchInput.length < 2 ? (
              <p className="mt-1 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {graphMessage(graphLocale, "extendSearchMinLength")}
              </p>
            ) : null}
            {searchError ? (
              <p className="mt-1 text-xs text-warning-red dark:text-warning-red-dark" role="alert">
                {searchError}
              </p>
            ) : null}
          </div>
          <div className="mt-3 flex flex-col gap-2" aria-live="polite">
            {searchLoading ? (
              <div className="flex items-center justify-center py-4 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                <Loader2 className="size-4 animate-spin" aria-hidden />
              </div>
            ) : null}
            {!searchLoading && searchResults.length === 0 && debouncedSearchInput.length >= 2 ? (
              <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {graphMessage(graphLocale, "extendNoResults")}
              </p>
            ) : null}
            {searchResults.map((entry) => {
              const id = Number(entry.post.id);
              const isSelected = selectedSourceIds.has(id);
              return (
                <div key={String(id)} className="graph-draft-extend-result">
                  {renderCompactCard(
                    entry.post as CollectionEntity,
                    entry.graph,
                    isSelected ? null : (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-full border border-eoe-accent bg-eoe-accent/10 px-2 py-1 text-xs font-semibold text-eoe-accent transition-colors hover:bg-eoe-accent/20 dark:border-eoe-dark-accent dark:text-eoe-dark-accent"
                        onClick={() => handleAddSource(entry)}
                        aria-label={graphMessage(graphLocale, "extendAddToSelection")}
                      >
                        <Plus className="size-3" aria-hidden />
                        <span>{graphMessage(graphLocale, "extendAddToSelection")}</span>
                      </button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {selectedSources.length > 0 ? (
          <section className="graph-draft-selected">
            <h3 className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
              {formatGraphMessage(graphLocale, "remixSourcesSelected", {
                count: selectedSources.length,
              })}
            </h3>
            <div className="mt-3 flex flex-col gap-2">
              {selectedSources.map(({ post, snapshot }) => {
                const id = Number(post.id);
                return (
                  <div key={String(id)} className="graph-draft-selected-entry">
                    {renderCompactCard(
                      post,
                      snapshot,
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-full border border-eoe-border-main bg-eoe-bg px-2 py-1 text-xs font-semibold text-eoe-text-sub transition-colors hover:bg-eoe-icon hover:text-eoe-text dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg dark:text-eoe-dark-text-sub dark:hover:bg-eoe-dark-icon dark:hover:text-eoe-dark-text"
                        onClick={() => handleRemoveSource(id)}
                        aria-label={graphMessage(graphLocale, "extendRemoveFromSelection")}
                      >
                        <X className="size-3" aria-hidden />
                        <span>{graphMessage(graphLocale, "extendRemoveFromSelection")}</span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}

        <section className="graph-draft-cta sticky bottom-4 z-10">
          <button
            type="button"
            className={`w-full rounded-full px-4 py-3 text-sm font-semibold shadow-sm transition-colors ${
              primaryCtaDisabled || isStartingDraft
                ? "btn-primary-disabled cursor-not-allowed opacity-50"
                : "btn-primary"
            }`}
            disabled={primaryCtaDisabled || isStartingDraft}
            onClick={() => void handlePrimaryAction()}
          >
            {isStartingDraft ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="size-4 animate-spin" aria-hidden />
              </span>
            ) : (
              primaryCtaLabel
            )}
          </button>
          {selectedIssue && selectedSources.length === 0 && resolvedIssueName ? (
            <p className="mt-2 text-center text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
              {resolvedIssueName}
            </p>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default GraphDraftEditor;
